<template>
  <section class="single-obituary-navigation" ref="root">
    <div class="single-obituary-navigation__wrapper" ref="wrapper">
      <div class="container single-obituary-navigation__quick-nav">
        <nav>
          <button
            class="btn link"
            type="button"
            v-for="(section, i) in sections"
            :key="i"
            :aria-selected="section.id === currentId"
            :ref="`${section.id}-link`"
            @click.prevent="scrollToSection(section)"
          >
            {{ section.dataset.quicknav }}
          </button>
        </nav>

        <navigation-actions :no-donations="noDonations" :order-id="orderId" />
      </div>
    </div>
  </section>
</template>

<script>
  import { useQuickNav } from "Main/composables/useQuickNav";

  import { ref } from "vue";
  import { mapState } from "vuex";
  import { EventBus } from "../event-bus";
  import NavigationActions from "./NavigationActions.vue";

  export default {
    beforeUnmount() {
      EventBus.off("navigate-to", this.externalSelectSection);
      window.removeEventListener("resize", this.initActiveSections);
    },

    components: { NavigationActions },

    computed: {
      ...mapState("sections", { asyncSections: "sections" }),
    },

    data() {
      const { orderId, noDonations } = this.$mountEl.dataset;

      return {
        clickToScroll: false,
        currentId: null,
        isStuck: false,
        lastScroll: 0,
        noDonations: noDonations === "true",
        orderId,
        texts: lavendlaObituary.common,
      };
    },

    methods: {
      checkAndMakeSectionActive(entries) {
        for (const entry of entries) {
          if (this.linkClicked || !entry.isIntersecting) {
            continue;
          }

          // Check if the quicknav id is set to the scroll offset div used on the city-pages.
          const sectionQuickNavTarget =
            entry.target.querySelector("[data-quicknav]");

          if (sectionQuickNavTarget) {
            this.currentId = sectionQuickNavTarget.id;
            continue;
          }

          this.currentId = entry.target.id;
        }
      },

      checkAndStickMenu(entries) {
        this.isStuck =
          entries[0].isIntersecting || this.isAboveInterceptors(entries[0]);
      },

      externalSelectSection(sectionId) {
        const section = document.querySelector(`#${sectionId}`);

        if (section) {
          this.scrollToSection(section);
        }
      },
      initActiveSections() {
        if (!this.isObserverSupported) {
          return false;
        }

        if (!this.sections.length) {
          return false;
        }

        const viewportHeight = Math.max(
          document.documentElement.clientHeight,
          window.innerHeight || 0
        );
        const activeSectionOffset =
          viewportHeight - this.qnHeight - this.headerHeight - 20;

        if (activeSectionOffset > 0) {
          const sectionObserver = new IntersectionObserver(
            this.checkAndMakeSectionActive,
            {
              rootMargin: `0px 0px -${activeSectionOffset}px`,
            }
          );

          for (const el of this.sections) {
            sectionObserver.observe(el.closest("section"));
          }
        }
      },

      initStickyMenuCheck() {
        if (!this.isObserverSupported) {
          return false;
        }

        if (!this.sections.length) {
          return false;
        }

        const viewportHeight = Math.max(
          document.documentElement.clientHeight,
          window.innerHeight || 0
        );
        const stickMenuOffset = viewportHeight - this.headerHeight;

        if (stickMenuOffset > 0) {
          const stickMenuObserver = new IntersectionObserver(
            this.checkAndStickMenu,
            {
              rootMargin: `0px 0px -${stickMenuOffset}px`,
            }
          );

          stickMenuObserver.observe(this.$refs.root);
        }
      },

      isAboveInterceptors(element) {
        if (!element.rootBounds) {
          return false;
        }

        const boundingClientRectY =
          typeof element.boundingClientRect.y === "undefined"
            ? element.boundingClientRect.top
            : element.boundingClientRect.y;

        const rootBoundsY =
          typeof element.rootBounds.y === "undefined"
            ? element.rootBounds.top
            : element.rootBounds.y;

        return boundingClientRectY < rootBoundsY;
      },

      scrollToSection(section) {
        const sectionOffset =
          section.getBoundingClientRect().top + window.scrollY;
        const offsetTop = Math.abs(sectionOffset - this.qnHeight);
        history.pushState(null, null, `#${section.id}`);

        this.linkClicked = true;
        this.currentId = section.id;

        window.scrollTo({
          behavior: "smooth",
          top: Number.parseInt(offsetTop + 2, 10),
        });

        setTimeout(() => {
          this.linkClicked = false;
        }, 1000);
      },
    },

    mounted() {
      this.initStickyMenuCheck();
      this.initActiveSections();

      EventBus.on("navigate-to", this.externalSelectSection);
      window.addEventListener("resize", this.initActiveSections);
    },
    // biome-ignore lint/style/useVueMultiWordComponentNames: the page registers it by this name.
    name: "Navigation",

    setup() {
      const root = ref(null);
      const {
        qnInitialOffset,
        qnHeight,
        headerHeight,
        sections,
        qnLinks,
        isObserverSupported,
        getSections,
        isElementVisible,
        initOffsets,
      } = useQuickNav(root);
      return {
        getSections,
        headerHeight,
        initOffsets,
        isElementVisible,
        isObserverSupported,
        qnHeight,
        qnInitialOffset,
        qnLinks,
        root,
        sections,
      };
    },

    watch: {
      // Sections shown after load (the memory book) are pushed onto the store's array: watch deep.
      asyncSections: {
        deep: true,
        handler(sections) {
          if (!sections) {
            return;
          }
          for (const section of sections) {
            if (!this.sections.includes(section)) {
              this.sections.push(section);
            }
          }
          // A section shown later (the memory book) takes its place in page order.
          this.sections.sort((a, b) =>
            // biome-ignore lint/suspicious/noBitwiseOperators: compareDocumentPosition answers with a bit mask.
            a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING
              ? -1
              : 1
          );

          this.initActiveSections();
        },
      },
    },
  };
</script>
